import React, { useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import toast from 'react-hot-toast';
import { getAllIbans, addIban, updateIban, deleteIban } from '../../api/ibans';
import { useLoader } from '../../context/LoaderContext';

const IbanInformation = () => {
    const { showLoader, hideLoader } = useLoader();
    const [ibans, setIbans] = useState([]);
    const [loading, setLoading] = useState(false);

    // Modal States
    const [showModal, setShowModal] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [formData, setFormData] = useState({
        iban: '',
        bankName: '',
        branchName: '',
        branchCode: '',
        accountHolderName: '',
        accountHolderSurname: '',
        isActive: true
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Delete Confirmation State
    const [deleteId, setDeleteId] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const fetchIbans = async () => {
        showLoader();
        try {
            const res = await getAllIbans();
            if (Array.isArray(res)) {
                setIbans(res);
            } else if (res.data && Array.isArray(res.data)) {
                setIbans(res.data);
            } else {
                setIbans([]);
            }
        } catch (error) {
            console.error("IBAN fetch error:", error);
            toast.error("IBAN listesi yüklenirken hata oluştu.");
        } finally {
            hideLoader();
        }
    };

    useEffect(() => {
        fetchIbans();
    }, []);

    // Form Handlers
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const openAddModal = () => {
        setIsEdit(false);
        setFormData({
            iban: '',
            bankName: '',
            branchName: '',
            branchCode: '',
            accountHolderName: '',
            accountHolderSurname: '',
            isActive: true
        });
        setShowModal(true);
    };

    const openEditModal = (ibanItem) => {
        setIsEdit(true);
        setFormData({
            id: ibanItem.id,
            iban: ibanItem.iban,
            bankName: ibanItem.bankName,
            branchName: ibanItem.branchName,
            branchCode: ibanItem.branchCode,
            accountHolderName: ibanItem.accountHolderName,
            accountHolderSurname: ibanItem.accountHolderSurname,
            isActive: ibanItem.isActive
        });
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        showLoader();
        try {
            // Extract id and prepare payload (DTO) without id
            const { id, ...payload } = formData;

            if (isEdit) {
                // For update, id is usually in URL. Payload should match DTO.
                await updateIban(id, payload);
                toast.success("IBAN başarıyla güncellendi.");
            } else {
                // For add, explicit payload without id
                await addIban(payload);
                toast.success("IBAN başarıyla eklendi.");
            }
            closeModal();
            fetchIbans();
        } catch (error) {
            console.error("Save error:", error);
            // Handle validation errors (e.g. 400 Bad Request)
            if (error.errors) {
                // Show first error message from response
                const firstError = Object.values(error.errors).flat()[0];
                toast.error(firstError || "Validasyon hatası.");
            } else {
                toast.error(error.message || "Kaydetme başarısız.");
            }
        } finally {
            hideLoader();
        }
    };

    // Delete Handlers
    const confirmDelete = (id) => {
        setDeleteId(id);
        setShowDeleteModal(true);
    };

    const performDelete = async () => {
        if (!deleteId) return;

        showLoader();
        try {
            await deleteIban(deleteId);
            toast.success("IBAN başarıyla silindi.");
            setShowDeleteModal(false);
            setDeleteId(null);
            fetchIbans();
        } catch (error) {
            console.error("Delete error:", error);
            toast.error(error.message || "Silme işlemi başarısız.");
        } finally {
            hideLoader();
        }
    };


    return (
        <>
            <div className="card h-100 p-0 radius-12">
                <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center justify-content-between">
                    <h6 className="text-lg fw-semibold mb-0">IBAN Bilgileri</h6>
                    <button onClick={openAddModal} className="btn btn-primary-600 radius-8 px-20 py-11 d-flex align-items-center gap-2">
                        <Icon icon="heroicons:plus-circle" className="text-xl" />
                        Yeni Ekle
                    </button>
                </div>
                <div className="card-body p-24">
                    <div className="table-responsive">
                        <table className="table bordered-table mb-0">
                            <thead>
                                <tr>
                                    <th scope="col">Banka Adı</th>
                                    <th scope="col">Şube</th>
                                    <th scope="col">Hesap Sahibi</th>
                                    <th scope="col">IBAN</th>
                                    <th scope="col">Durum</th>
                                    <th scope="col" className="text-center">İşlemler</th>
                                </tr>
                            </thead>
                            <tbody>
                                {ibans.length > 0 ? (
                                    ibans.map((item) => (
                                        <tr key={item.id}>
                                            <td><span className="text-primary-600 fw-bold">{item.bankName}</span></td>
                                            <td>
                                                <div className="d-flex flex-column">
                                                    <span className="text-secondary-light fw-medium">{item.branchName}</span>
                                                    <span className="text-xs text-secondary-light opacity-75">Kod: {item.branchCode}</span>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="d-flex flex-column">
                                                    <span className="text-secondary-light fw-medium">{item.accountHolderName} {item.accountHolderSurname}</span>
                                                </div>
                                            </td>
                                            <td><span className="text-secondary-light font-monospace">{item.iban}</span></td>
                                            <td>
                                                {item.isActive ? (
                                                    <span className="badge bg-success-focus text-success-main px-12 py-6 radius-4">Aktif</span>
                                                ) : (
                                                    <span className="badge bg-danger-focus text-danger-main px-12 py-6 radius-4">Pasif</span>
                                                )}
                                            </td>
                                            <td className="text-center">
                                                <div className="d-flex align-items-center justify-content-center gap-2">
                                                    <button
                                                        onClick={() => openEditModal(item)}
                                                        className="w-32-px h-32-px bg-primary-50 text-primary-600 rounded-circle d-inline-flex align-items-center justify-content-center hover-bg-primary-600 hover-text-white transition-2"
                                                        title="Düzenle"
                                                    >
                                                        <Icon icon="heroicons:pencil-square" className="text-xl" />
                                                    </button>
                                                    <button
                                                        onClick={() => confirmDelete(item.id)}
                                                        className="w-32-px h-32-px bg-danger-50 text-danger-600 rounded-circle d-inline-flex align-items-center justify-content-center hover-bg-danger-600 hover-text-white transition-2"
                                                        title="Sil"
                                                    >
                                                        <Icon icon="heroicons:trash" className="text-xl" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="6" className="text-center py-24">
                                            <p className="text-secondary-light mb-0">Kayıtlı IBAN bulunamadı.</p>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Add/Edit Modal */}
            {showModal && (
                <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
                    <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
                        <div className="modal-content radius-16">
                            <div className="modal-header">
                                <h5 className="modal-title">{isEdit ? 'IBAN Düzenle' : 'Yeni IBAN Ekle'}</h5>
                                <button type="button" className="btn-close" onClick={closeModal}></button>
                            </div>
                            <form onSubmit={handleSubmit}>
                                <div className="modal-body">
                                    <div className="row g-3">
                                        <div className="col-md-6">
                                            <label className="form-label fw-semibold">Banka Adı</label>
                                            <input type="text" className="form-control" name="bankName" value={formData.bankName} onChange={handleInputChange} required placeholder="Örn: Ziraat Bankası" />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label fw-semibold">Şube Adı</label>
                                            <input type="text" className="form-control" name="branchName" value={formData.branchName} onChange={handleInputChange} required placeholder="Örn: Merkez Şube" />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label fw-semibold">Şube Kodu</label>
                                            <input type="text" className="form-control" name="branchCode" value={formData.branchCode} onChange={handleInputChange} required placeholder="Örn: 1234" />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label fw-semibold">IBAN</label>
                                            <input type="text" className="form-control" name="iban" value={formData.iban} onChange={handleInputChange} required placeholder="TR..." />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label fw-semibold">Hesap Sahibi Adı</label>
                                            <input type="text" className="form-control" name="accountHolderName" value={formData.accountHolderName} onChange={handleInputChange} required placeholder="Ad" />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label fw-semibold">Hesap Sahibi Soyadı</label>
                                            <input type="text" className="form-control" name="accountHolderSurname" value={formData.accountHolderSurname} onChange={handleInputChange} required placeholder="Soyad" />
                                        </div>
                                        <div className="col-12">
                                            <div className="form-check">
                                                <input className="form-check-input" type="checkbox" name="isActive" id="isActiveCheck" checked={formData.isActive} onChange={handleInputChange} />
                                                <label className="form-check-label fw-medium" htmlFor="isActiveCheck">
                                                    Aktif Olarak İşaretle
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={closeModal}>İptal</button>
                                    <button type="submit" className="btn btn-primary-600" disabled={isSubmitting}>
                                        {isSubmitting ? 'Kaydediliyor...' : 'Kaydet'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
                    <div className="modal-dialog modal-dialog-centered" role="document">
                        <div className="modal-content radius-16">
                            <div className="modal-header bg-danger-focus text-danger-main">
                                <h5 className="modal-title d-flex align-items-center gap-2">
                                    <Icon icon="heroicons:exclamation-triangle" />
                                    IBAN Silme Onayı
                                </h5>
                                <button type="button" className="btn-close" onClick={() => setShowDeleteModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                <p className="mb-0 fw-medium">Bu IBAN bilgisini silmek istediğinize emin misiniz? Bu işlem geri alınamaz.</p>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>Vazgeç</button>
                                <button type="button" className="btn btn-danger-600" onClick={performDelete}>Evet, Sil</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default IbanInformation;
